import { useEffect, useState, type RefObject } from "react";

// Menú lateral que se expande y se contrae solo, como el del Company Portal (feedback.js del portal: prepareRail).
// Plegado es un riel de íconos de 72 px; se expande encima del contenido (no lo empuja) y nada se recuerda entre visitas:
// - mouse: se abre 120 ms después de entrar y se cierra 500 ms después de salir (un paso rápido por encima no lo abre);
// - teclado: con Tab, el foco dentro del menú lo abre y salir del menú lo cierra; el foco que deja un clic no lo abre;
// - Esc, un clic fuera o ir a otra pantalla lo cierran;
// - ☰ (escritorio y pantallas táctiles anchas, sin mouse que pase por encima) lo abre o lo cierra a mano.

/** Espera antes de abrir con el mouse encima (ms): la del Company Portal. */
export const RAIL_OPEN_DELAY = 120;
/** Espera antes de cerrar al salir el mouse (ms): la del Company Portal. */
export const RAIL_CLOSE_DELAY = 500;

/** Desde 768 px hay menú lateral; en el celular las áreas van en la hoja de ☰ (C47). */
const wide = () => typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 768px)").matches;

/** Hay una ventana o una hoja encima: su Esc es suyo. */
const modalOpen = () => !!document.querySelector("[aria-modal='true'], dialog:modal");

/**
 * Estado del menú lateral: `expanded` y `toggle` (para ☰). `here` es la dirección actual: al cambiar, el menú vuelve a
 * plegarse (el estado guarda la dirección en que se abrió). `opener` es ☰: tocarlo no cuenta como clic fuera.
 */
export function useRailExpansion(
  rail: RefObject<HTMLElement | null>,
  opener: RefObject<HTMLElement | null>,
  here: string,
) {
  const [openAt, setOpenAt] = useState<string | null>(null);
  const expanded = openAt === here;

  useEffect(() => {
    const node = rail.current;
    if (!node) return;
    let openTimer = 0;
    let closeTimer = 0;
    let keyboard = false;
    let pointerInside = false;
    const clear = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };
    const open = () => setOpenAt(here);
    const close = () => setOpenAt(null);

    const onEnter = (event: PointerEvent) => {
      if (event.pointerType !== "mouse" || !wide()) return;
      pointerInside = true;
      clear();
      openTimer = window.setTimeout(open, RAIL_OPEN_DELAY);
    };
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      pointerInside = false;
      clear();
      // Con el foco de teclado adentro, sacar el mouse no lo cierra.
      if (keyboard && node.contains(document.activeElement)) return;
      closeTimer = window.setTimeout(close, RAIL_CLOSE_DELAY);
    };
    const onFocusIn = () => {
      if (!keyboard || !wide()) return;
      clear();
      open();
    };
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null;
      if (next && (node.contains(next) || opener.current?.contains(next))) return;
      if (pointerInside) return;
      clear();
      close();
    };
    const onDown = (event: PointerEvent) => {
      keyboard = false;
      const target = event.target as Node;
      if (node.contains(target) || opener.current?.contains(target)) return;
      clear();
      pointerInside = false;
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        keyboard = true;
        if (wide() && node.contains(document.activeElement)) {
          clear();
          open();
        }
      } else if (event.key === "Escape" && !modalOpen()) {
        clear();
        close();
      }
    };

    node.addEventListener("pointerenter", onEnter);
    node.addEventListener("pointerleave", onLeave);
    node.addEventListener("focusin", onFocusIn);
    node.addEventListener("focusout", onFocusOut);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      clear();
      node.removeEventListener("pointerenter", onEnter);
      node.removeEventListener("pointerleave", onLeave);
      node.removeEventListener("focusin", onFocusIn);
      node.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [rail, opener, here]);

  /** ☰: abre y lleva el foco a la primera área (si fue con teclado, el anillo se ve), o cierra. */
  const toggle = () => {
    if (expanded) {
      setOpenAt(null);
      return;
    }
    setOpenAt(here);
    requestAnimationFrame(() => rail.current?.querySelector<HTMLElement>("a[href]")?.focus());
  };

  return { expanded, toggle };
}
